import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

const defaultTimeSlots = [
  { label: "09:00 AM", startHour: 9, startMin: 0, endHour: 9, endMin: 30 },
  { label: "09:30 AM", startHour: 9, startMin: 30, endHour: 10, endMin: 0 },
  { label: "10:00 AM", startHour: 10, startMin: 0, endHour: 10, endMin: 30 },
  { label: "10:30 AM", startHour: 10, startMin: 30, endHour: 11, endMin: 0 },
  { label: "11:00 AM", startHour: 11, startMin: 0, endHour: 11, endMin: 30 },
  { label: "11:30 AM", startHour: 11, startMin: 30, endHour: 12, endMin: 0 },
  { label: "01:00 PM", startHour: 13, startMin: 0, endHour: 13, endMin: 30 },
  { label: "01:30 PM", startHour: 13, startMin: 30, endHour: 14, endMin: 0 },
  { label: "02:00 PM", startHour: 14, startMin: 0, endHour: 14, endMin: 30 },
  { label: "02:30 PM", startHour: 14, startMin: 30, endHour: 15, endMin: 0 },
  { label: "03:00 PM", startHour: 15, startMin: 0, endHour: 15, endMin: 30 },
  { label: "03:30 PM", startHour: 15, startMin: 30, endHour: 16, endMin: 0 },
  { label: "04:00 PM", startHour: 16, startMin: 0, endHour: 16, endMin: 30 },
  { label: "04:30 PM", startHour: 16, startMin: 30, endHour: 17, endMin: 0 },
];

const AddSechduleTime = ({ secShowHide, handleSecShowHide, handleSubmitSchedule }) => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  const [customStart, setCustomStart] = useState("09:00");
  const [customEnd, setCustomEnd] = useState("09:30");
  const [customError, setCustomError] = useState("");

  // Reset state when the modal is opened
  useEffect(() => {
    if (secShowHide) {
      setEvents([]);
      setSelectedDate(new Date());
      setCustomStart("09:00");
      setCustomEnd("09:30");
      setCustomError("");
    }
  }, [secShowHide]);

  const getStartOfDay = (date) => {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day;
  };

  const isPastDate = (date) => getStartOfDay(date).getTime() < getStartOfDay(new Date()).getTime();

  const getSlotDateTime = (slot) => {
    const slotDate = new Date(selectedDate);
    slotDate.setHours(slot.startHour, slot.startMin, 0, 0);
    return slotDate;
  };

  const isPastSlot = (slot) => getSlotDateTime(slot).getTime() <= new Date().getTime();

  const getCurrentTimeValue = () => moment().format("HH:mm");

  const handleSelectDate = (obj) => {
    if (isPastDate(obj.start)) {
      setCustomError("Past dates are not available for scheduling.");
      return;
    }

    setCustomError("");
    setSelectedDate(obj.start);
  };

  const handleDateChanged = (date) => {
    const nextDate = isPastDate(date) ? new Date() : date;
    setSelectedDate(nextDate);
  };

  const isSelected = (slot) => {
    return events.some((e) => {
      const eStart = new Date(e.start);
      return (
        eStart.getFullYear() === selectedDate.getFullYear() &&
        eStart.getMonth() === selectedDate.getMonth() &&
        eStart.getDate() === selectedDate.getDate() &&
        eStart.getHours() === slot.startHour &&
        eStart.getMinutes() === slot.startMin
      );
    });
  };

  const handleToggleSlot = (slot) => {
    if (isPastDate(selectedDate) || isPastSlot(slot)) {
      setCustomError("Past time slots are not available for scheduling.");
      return;
    }

    setCustomError("");
    const isAlreadySelected = isSelected(slot);
    if (isAlreadySelected) {
      const updated = events.filter((e) => {
        const eStart = new Date(e.start);
        const matches = (
          eStart.getFullYear() === selectedDate.getFullYear() &&
          eStart.getMonth() === selectedDate.getMonth() &&
          eStart.getDate() === selectedDate.getDate() &&
          eStart.getHours() === slot.startHour &&
          eStart.getMinutes() === slot.startMin
        );
        return !matches;
      });
      setEvents(updated);
    } else {
      const startDt = new Date(selectedDate);
      startDt.setHours(slot.startHour, slot.startMin, 0, 0);

      const endDt = new Date(selectedDate);
      endDt.setHours(slot.endHour, slot.endMin, 0, 0);

      let lastId = 1;
      if (events.length > 0) {
        lastId = events[events.length - 1].optionId + 1;
      }

      const newEvent = {
        optionId: lastId,
        start: startDt,
        end: endDt,
        title: "Meeting",
        slots: [startDt, endDt],
      };
      setEvents([...events, newEvent]);
    }
  };

  const handleAddCustomSlot = () => {
    setCustomError("");
    if (!customStart || !customEnd) {
      setCustomError("Please pick start and end times.");
      return;
    }
    const [sHour, sMin] = customStart.split(":").map(Number);
    const [eHour, eMin] = customEnd.split(":").map(Number);

    if (sHour > eHour || (sHour === eHour && sMin >= eMin)) {
      setCustomError("Start time must be before end time.");
      return;
    }

    const startDt = new Date(selectedDate);
    startDt.setHours(sHour, sMin, 0, 0);

    const endDt = new Date(selectedDate);
    endDt.setHours(eHour, eMin, 0, 0);

    if (isPastDate(selectedDate) || startDt.getTime() <= new Date().getTime()) {
      setCustomError("Start time must be in the future.");
      return;
    }

    // Prevent duplicates
    const isDuplicate = events.some((e) => {
      return (
        e.start.getTime() === startDt.getTime() &&
        e.end.getTime() === endDt.getTime()
      );
    });

    if (isDuplicate) {
      setCustomError("This time slot is already added.");
      return;
    }

    let lastId = 1;
    if (events.length > 0) {
      lastId = events[events.length - 1].optionId + 1;
    }

    const newEvent = {
      optionId: lastId,
      start: startDt,
      end: endDt,
      title: "Meeting",
      slots: [startDt, endDt],
    };
    setEvents([...events, newEvent]);
  };

  const getSelectedSlotsForDate = () => {
    return events.filter((e) => {
      const eStart = new Date(e.start);
      return (
        eStart.getFullYear() === selectedDate.getFullYear() &&
        eStart.getMonth() === selectedDate.getMonth() &&
        eStart.getDate() === selectedDate.getDate()
      );
    }).sort((a, b) => a.start.getTime() - b.start.getTime());
  };

  const formatTime = (dateObj) => {
    return new Date(dateObj).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const doSubmit = () => {
    const upcomingEvents = events.filter((event) => new Date(event.start).getTime() > new Date().getTime());
    if (upcomingEvents.length !== events.length) {
      setEvents(upcomingEvents);
    }

    handleSubmitSchedule(upcomingEvents);
  };

  const localizer = momentLocalizer(moment);
  const formattedSelectedDate = moment(selectedDate).format("dddd, MMMM DD");

  return (
    <>
      <Modal
        show={secShowHide}
        size="lg"
        onHide={handleSecShowHide}
        centered
        scrollable
        dialogClassName="rt-add-schedule-modal"
        backdropClassName="rt-add-schedule-backdrop"
        style={{ zIndex: 1110 }}
      >
                <style>{`
          .modal:has(.rt-add-schedule-modal) {
            z-index: 1110 !important;
          }
          .modal-dialog-centered.modal-dialog-scrollable.rt-add-schedule-modal {
            height: auto !important;
          }
          .rt-add-schedule-modal .modal-content {
            max-height: calc(100vh - 60px) !important;
            height: auto !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .rt-add-schedule-backdrop {
            z-index: 1100 !important;
            backdrop-filter: blur(4px) !important;
            -webkit-backdrop-filter: blur(4px) !important;
            background-color: rgba(15, 23, 42, 0.3) !important;
            opacity: 1 !important;
          }

          :root {
            --primary-color: #2563eb;
            --primary-hover: #1d4ed8;
            --primary-soft: #eff6ff;
            --primary-glow: rgba(37, 99, 235, 0.15);
            --secondary-color: #64748b;
            --secondary-hover: #475569;
            --border-color: #e2e8f0;
            --bg-light: #f8fafc;
            --text-main: #0f172a;
            --text-muted: #64748b;
          }

          @media (min-width: 992px) {
            .modal-lg {
              max-width: 1040px !important;
              width: 1040px !important;
            }
          }

          .modal-content {
            border-radius: 0 !important;
            border: 1px solid #cbd5e1 !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05) !important;
            overflow: hidden !important;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
          }

          .modal-header {
            position: relative !important;
            display: block !important;
            padding: 16px 20px !important;
            background: #ffffff !important;
            border-bottom: 1px solid #e2e8f0 !important;
            overflow: hidden !important;
          }


          .rt-header-shell {
            display: flex !important;
            flex-direction: column !important;
            gap: 4px !important;
            width: 100% !important;
          }

          .rt-header-top {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            gap: 16px !important;
            width: 100% !important;
          }

          .rt-header-main {
            display: flex !important;
            align-items: center !important;
            gap: 12px !important;
            min-width: 0 !important;
            flex: 1 1 auto !important;
          }

          .rt-header-icon {
            width: 36px !important;
            height: 36px !important;
            min-height: 36px !important;
            border-radius: 0 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            color: #1e293b !important;
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            box-shadow: 0 1px 2px rgba(15,23,42,0.04) !important;
            font-size: 15px !important;
            flex-shrink: 0 !important;
          }

          .rt-header-copy {
            display: flex !important;
            flex-direction: column !important;
            gap: 2px !important;
            min-width: 0 !important;
          }

          .rt-modal-title {
            display: inline-flex !important;
            align-items: center !important;
            margin: 0 !important;
            color: #1e293b !important;
            font-size: 16px !important;
            font-weight: 700 !important;
            line-height: 1.2 !important;
            min-width: 0 !important;
          }

          .rt-modal-title-text {
            display: inline-flex !important;
            align-items: center !important;
            gap: 8px !important;
            min-width: 0 !important;
            flex-wrap: wrap !important;
          }

          .rt-modal-title-badge {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 2px 6px !important;
            border-radius: 0 !important;
            font-size: 11px !important;
            font-weight: 600 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.02em !important;
            background: #eff6ff !important;
            color: #1e40af !important;
            border: 1px solid #bfdbfe !important;
          }

          .rt-modal-meta-row {
            display: flex !important;
            align-items: center !important;
            flex-wrap: wrap !important;
            gap: 8px !important;
            margin-top: 0 !important;
            font-size: 12.5px !important;
            color: #64748b !important;
          }

          .rt-meta-desc {
            display: inline-flex !important;
            align-items: center !important;
            gap: 6px !important;
            color: #64748b !important;
            font-weight: 400 !important;
            font-size: 13px !important;
          }

          .rt-action-btn {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 32px !important;
            height: 32px !important;
            padding: 0 !important;
            font-size: 13.5px !important;
            font-weight: 500 !important;
            color: #64748b !important;
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 0 !important;
            cursor: pointer !important;
            transition: all 0.2s ease !important;
            box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
            outline: none !important;
          }

          .rt-action-btn:hover {
            color: #dc2626 !important;
            border-color: #fca5a5 !important;
            background: #fef2f2 !important;
            transform: translateY(-1px) !important;
          }

          .modal-body {
            padding: 20px !important;
            background: #ffffff !important;
          }

          .modal-footer {
            border-top: 1px solid #e2e8f0 !important;
            padding: 14px 20px !important;
            background: #ffffff !important;
            display: flex !important;
            justify-content: flex-end !important;
            gap: 10px !important;
          }

          /* React Big Calendar Overrides */
          .rbc-calendar {
            font-family: inherit !important;
          }

          .rbc-toolbar {
            margin-bottom: 12px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            flex-wrap: wrap !important;
            gap: 8px !important;
          }

          .rbc-btn-group {
            display: inline-flex !important;
            gap: 6px !important;
          }

          .rbc-btn-group button {
            color: #1e293b !important;
            border: 1px solid #cbd5e1 !important;
            background: #ffffff !important;
            padding: 5px 12px !important;
            border-radius: 0 !important;
            font-weight: 500 !important;
            font-size: 12.5px !important;
            transition: all 0.15s ease !important;
            outline: none !important;
            margin: 0 !important;
            box-shadow: 0 1px 2px rgba(15,23,42,0.04) !important;
          }

          .rbc-btn-group button:hover {
            background: #f8fafc !important;
            color: #0f172a !important;
            border-color: #94a3b8 !important;
          }

          .rbc-btn-group button:active, .rbc-btn-group button.rbc-active {
            background: #2563eb !important;
            color: #ffffff !important;
            border-color: #2563eb !important;
          }

          .rbc-toolbar-label {
            font-weight: 700 !important;
            font-size: 14px !important;
            color: #1e293b !important;
          }

          .rbc-header {
            font-size: 11px !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.04em !important;
            color: #475569 !important;
            background: #f8fafc !important;
            padding: 8px 0 !important;
            border-bottom: 1px solid #cbd5e1 !important;
          }

          .rbc-month-view {
            border: 1px solid #cbd5e1 !important;
            border-radius: 0 !important;
            overflow: hidden !important;
            background: #ffffff !important;
          }

          .rbc-day-bg {
            border-left: 1px solid #e2e8f0 !important;
            transition: all 0.15s ease !important;
          }

          .rbc-month-row {
            border-top: 1px solid #e2e8f0 !important;
          }

          .rbc-day-bg:hover {
            background-color: #f8fafc !important;
            cursor: pointer !important;
          }

          .bg-selected-day {
            background-color: #eff6ff !important;
            box-shadow: inset 0 0 0 2px #2563eb !important;
            border-radius: 0 !important;
          }

          .rbc-today {
            background-color: #fef9c3 !important;
          }

          .bg-has-slots {
            background-color: #f0fdf4 !important;
            border: 1px dashed #86efac !important;
            border-radius: 0 !important;
          }

          .calendar-col-title {
            font-size: 12px !important;
            font-weight: 700 !important;
            color: #334155 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.04em !important;
            margin-bottom: 10px !important;
            display: flex !important;
            align-items: center !important;
            gap: 6px !important;
          }

          /* Time Slots Grid */
          .time-slots-grid {
            display: grid !important;
            grid-template-columns: repeat(auto-fill, minmax(92px, 1fr)) !important;
            gap: 8px !important;
            margin-top: 4px !important;
          }

          .time-slot-pill {
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            color: #1e293b !important;
            border-radius: 0 !important;
            padding: 7px 10px !important;
            font-size: 13px !important;
            font-weight: 500 !important;
            text-align: center !important;
            cursor: pointer !important;
            transition: all 0.15s ease !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 6px !important;
            user-select: none !important;
            box-shadow: 0 1px 2px rgba(15,23,42,0.04) !important;
          }

          .time-slot-pill:hover {
            border-color: #94a3b8 !important;
            background: #f8fafc !important;
            color: #0f172a !important;
            transform: translateY(-1px) !important;
          }

          .time-slot-pill.is-selected {
            background: #2563eb !important;
            border-color: #2563eb !important;
            color: #ffffff !important;
            box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25) !important;
          }

          /* Custom slot builder styling */
          .custom-slot-builder {
            margin-top: 14px !important;
            padding-top: 12px !important;
            border-top: 1px solid #e2e8f0 !important;
          }

          .custom-slot-builder-title {
            font-size: 11.5px !important;
            font-weight: 700 !important;
            color: #334155 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.04em !important;
            margin-bottom: 8px !important;
            display: flex !important;
            align-items: center !important;
            gap: 6px !important;
          }

          .custom-slot-builder-fields {
            display: flex !important;
            align-items: flex-end !important;
            gap: 10px !important;
          }

          .custom-time-input-group {
            display: flex !important;
            flex-direction: column !important;
            gap: 4px !important;
            flex: 1 !important;
          }

          .custom-time-input-group label {
            font-size: 11px !important;
            font-weight: 700 !important;
            color: #475569 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.03em !important;
            margin-bottom: 0 !important;
          }

          .custom-time-input-group input {
            border: 1px solid #cbd5e1 !important;
            border-radius: 0 !important;
            padding: 6px 10px !important;
            font-size: 13px !important;
            font-weight: 500 !important;
            color: #1e293b !important;
            outline: none !important;
            transition: border-color 0.2s ease !important;
            background: #ffffff !important;
            width: 100% !important;
            box-shadow: 0 1px 2px rgba(15,23,42,0.04) !important;
          }

          .custom-time-input-group input:focus {
            border-color: #2563eb !important;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important;
          }

          .wf-btn-add-custom {
            background: #2563eb !important;
            color: #ffffff !important;
            border: 1px solid #2563eb !important;
            padding: 7px 14px !important;
            border-radius: 0 !important;
            font-size: 13px !important;
            font-weight: 500 !important;
            cursor: pointer !important;
            transition: all 0.2s ease !important;
            white-space: nowrap !important;
            outline: none !important;
            box-shadow: 0 2px 5px rgba(37, 99, 235, 0.2) !important;
          }

          .wf-btn-add-custom:hover {
            background: #1d4ed8 !important;
            border-color: #1d4ed8 !important;
            transform: translateY(-1px) !important;
          }

          .custom-error-text {
            color: #ef4444 !important;
            font-size: 12px !important;
            font-weight: 500 !important;
            margin-top: 6px !important;
          }

          /* Selected tags style */
          .selected-slots-section {
            margin-top: 12px !important;
            background: #f8fafc !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 0 !important;
            padding: 8px 12px !important;
          }

          .selected-slots-header {
            font-size: 11px !important;
            font-weight: 700 !important;
            color: #475569 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.03em !important;
            margin-bottom: 6px !important;
          }

          .selected-slots-tags {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 6px !important;
          }

          .selected-slot-tag {
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            color: #1e293b !important;
            border-radius: 0 !important;
            padding: 3px 8px !important;
            font-size: 12px !important;
            font-weight: 500 !important;
            display: inline-flex !important;
            align-items: center !important;
            gap: 6px !important;
            box-shadow: 0 1px 2px rgba(15,23,42,0.04) !important;
          }

          .remove-slot-tag-btn {
            border: none !important;
            background: transparent !important;
            color: #64748b !important;
            font-size: 14px !important;
            cursor: pointer !important;
            padding: 0 !important;
            line-height: 1 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
          }

          .remove-slot-tag-btn:hover {
            color: #ef4444 !important;
          }

          /* Footer buttons */
          .wf-btn {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
            padding: 7px 18px !important;
            font-size: 13.5px !important;
            font-weight: 500 !important;
            border-radius: 0 !important;
            cursor: pointer !important;
            transition: all 0.2s ease !important;
            border: 1px solid transparent !important;
            min-width: 110px !important;
            outline: none !important;
            box-shadow: 0 2px 5px rgba(15, 23, 42, 0.06) !important;
          }

          .wf-btn-primary {
            background: #2563eb !important;
            color: #ffffff !important;
            border-color: #2563eb !important;
            box-shadow: 0 2px 5px rgba(37, 99, 235, 0.2) !important;
          }

          .wf-btn-primary:hover {
            background: #1d4ed8 !important;
            border-color: #1d4ed8 !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3) !important;
          }

          .wf-btn-secondary {
            background: #ffffff !important;
            color: #1e293b !important;
            border-color: #cbd5e1 !important;
          }

          .wf-btn-secondary:hover {
            background: #f8fafc !important;
            color: #0f172a !important;
            border-color: #94a3b8 !important;
            transform: translateY(-1px) !important;
          }
        `}</style>
        <Modal.Header>
          <div className="rt-header-shell">
            <div className="rt-header-top">
              <div className="rt-header-main">
                <i className="rt-header-icon fa fa-calendar-plus-o" aria-hidden="true" />
                <div className="rt-header-copy">
                  <h2 className="rt-modal-title">
                    <span className="rt-modal-title-text">
                      <span>Add Meeting Slots</span>
                      <span className="rt-modal-title-badge">Scheduling Tool</span>
                    </span>
                  </h2>
                  <div className="rt-modal-meta-row">
                    <span className="rt-meta-desc">
                      <i className="fa fa-info-circle" aria-hidden="true" /> Propose one or more dates and times for the applicant to choose.
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="rt-action-btn"
                onClick={handleSecShowHide}
                title="Close"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6" style={{ height: "480px", display: "flex", flexDirection: "column" }}>
              <div className="calendar-col-title">
                <i className="fa fa-calendar" /> 1. Select Date
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <Calendar
                  selectable
                  localizer={localizer}
                  events={[]}
                  defaultView={Views.MONTH}
                  date={selectedDate}
                  views={["month"]}
                  onSelectSlot={handleSelectDate}
                  onNavigate={handleDateChanged}
                  dayPropGetter={(date) => {
                    const isSelectedDate = moment(date).isSame(selectedDate, "day");
                    const hasSlots = events.some((e) => moment(e.start).isSame(date, "day"));
                    
                    let classes = [];
                    if (isSelectedDate) {
                      classes.push("bg-selected-day");
                    } else if (hasSlots) {
                      classes.push("bg-has-slots");
                    }
                    
                    return { className: classes.join(" ") };
                  }}
                />
              </div>
            </div>
            <div className="col-md-6" style={{ height: "480px", display: "flex", flexDirection: "column" }}>
              <div className="calendar-col-title">
                <i className="fa fa-clock-o" /> 2. Pick Time Slots ({formattedSelectedDate})
              </div>
              <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <div className="time-slots-grid">
                  {defaultTimeSlots.map((slot, index) => {
                    const active = isSelected(slot);
                    return (
                      <div
                        key={index}
                        className={`time-slot-pill ${active ? "is-selected" : ""}`}
                        onClick={() => handleToggleSlot(slot)}
                      >
                        {active && <i className="fa fa-check" />}
                        {slot.label}
                      </div>
                    );
                  })}
                </div>

                <div className="custom-slot-builder">
                  <div className="custom-slot-builder-title">
                    <i className="fa fa-sliders" /> Custom Time Range
                  </div>
                  <div className="custom-slot-builder-fields">
                    <div className="custom-time-input-group">
                      <label>Start</label>
                      <input
                        type="time"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                      />
                    </div>
                    <div className="custom-time-input-group">
                      <label>End</label>
                      <input
                        type="time"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="wf-btn-add-custom"
                      onClick={handleAddCustomSlot}
                    >
                      <i className="fa fa-plus" /> Add Range
                    </button>
                  </div>
                  {customError && <div className="custom-error-text">{customError}</div>}
                </div>

                {getSelectedSlotsForDate().length > 0 && (
                  <div className="selected-slots-section">
                    <div className="selected-slots-header">Selected for this day:</div>
                    <div className="selected-slots-tags">
                      {getSelectedSlotsForDate().map((e) => (
                        <span key={e.optionId} className="selected-slot-tag">
                          <i className="fa fa-clock-o" />
                          {formatTime(e.start)} - {formatTime(e.end)}
                          <button
                            type="button"
                            className="remove-slot-tag-btn"
                            onClick={() => {
                              const updated = events.filter((x) => x.optionId !== e.optionId);
                              setEvents(updated);
                            }}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="wf-btn wf-btn-secondary" onClick={handleSecShowHide} type="button">
            <i className="fa fa-times" aria-hidden="true"></i> Close
          </button>
          <button className="wf-btn wf-btn-primary" onClick={doSubmit} type="button">
            <i className="fa fa-check" aria-hidden="true"></i> Save Slots
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddSechduleTime;
